import { useEffect, useState } from "react";
import { Camera, ImagePlus, MapPin, X, Clock, Trash2 } from "lucide-react";
import { fileUrl, dateTime } from "@/lib/api";

export function useGeo() {
  const [geo, setGeo] = useState({ status: "idle" });
  useEffect(() => {
    if (!navigator.geolocation) { setGeo({ status: "unsupported" }); return; }
    setGeo({ status: "locating" });
    navigator.geolocation.getCurrentPosition(
      p => setGeo({ status: "ok", lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }),
      () => setGeo({ status: "denied" }), { enableHighAccuracy: true, timeout: 8000 });
  }, []);
  return geo;
}

export function PhotoPicker({ files, setFiles, geo, label = "" }) {
  const add = (e) => { const list = Array.from(e.target.files || []).map(f => ({ file: f, preview: URL.createObjectURL(f), taken_at: new Date(f.lastModified).toISOString() })); setFiles([...files, ...list]); e.target.value = ""; };
  const remove = (i) => setFiles(files.filter((_, j) => j !== i));
  return (
    <div className="form-full photo-picker" data-testid="photo-picker">
      {label && <small style={{display:"block", marginBottom:6, color:"#5b6878", fontWeight:600}}>{label}</small>}
      <div className="photo-picker-head">
        <div style={{display:"flex", gap:8, flexWrap:"wrap"}}>
          <label className="ghost-btn" style={{cursor:"pointer"}} data-testid="photo-input-label">
            <Camera size={14} /> Ambil foto (kamera)
            <input data-testid="photo-input" type="file" accept="image/*" capture="environment" multiple hidden onChange={add} />
          </label>
          <label className="ghost-btn" style={{cursor:"pointer"}} data-testid="photo-gallery-input-label">
            <ImagePlus size={14} /> Upload dari galeri
            <input data-testid="photo-gallery-input" type="file" accept="image/*" multiple hidden onChange={add} />
          </label>
        </div>
        {geo.status !== "hidden" && <small className={`geo-badge geo-${geo.status}`} data-testid="geo-status">
          <MapPin size={11} /> {geo.status === "ok" ? `${geo.lat.toFixed(5)}, ${geo.lng.toFixed(5)} (±${Math.round(geo.accuracy)}m)` : geo.status === "locating" ? "Mencari lokasi GPS…" : geo.status === "denied" ? "GPS ditolak — foto tanpa koordinat" : "GPS tidak tersedia"}
        </small>}
      </div>
      {files.length > 0 && <div className="photo-grid">
        {files.map((f, i) => (
          <div key={i} className="photo-thumb" data-testid={`photo-preview-${i}`}>
            <img src={f.preview} alt="" />
            <button type="button" onClick={() => remove(i)} className="photo-remove"><X size={12} /></button>
          </div>
        ))}
      </div>}
    </div>
  );
}

export async function uploadPhotos(reportId, files, geo, upload, onProgress) {
  for (let i = 0; i < files.length; i++) {
    const fd = new FormData();
    fd.append("file", files[i].file);
    if (geo.status === "ok") { fd.append("lat", geo.lat); fd.append("lng", geo.lng); }
    fd.append("taken_at", files[i].taken_at);
    await upload(`/daily-reports/${reportId}/photos`, fd);
    onProgress?.(i + 1, files.length);
  }
}

export function TwoSlotPicker({ files, setFiles, labels = ["Foto 1", "Foto 2"] }) {
  const pick = (idx, e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    const next = [...files]; next[idx] = { file: f, preview: URL.createObjectURL(f), taken_at: new Date(f.lastModified).toISOString() };
    setFiles(next);
  };
  const clear = (idx) => { const next = [...files]; next[idx] = null; setFiles(next); };
  return (
    <div className="two-slot" data-testid="two-slot-picker">
      {[0, 1].map(i => {
        const f = files[i];
        return (
          <div className={`slot ${f ? "filled" : ""}`} key={i} data-testid={`receipt-slot-${i + 1}`}>
            {f ? <>
              <img src={f.preview} alt="" />
              <button type="button" className="slot-remove" data-testid={`receipt-slot-${i + 1}-remove`} onClick={() => clear(i)} title="Hapus"><X size={12} /></button>
              <span className="slot-name">{labels[i]} · {f.file.name}</span>
            </> : <>
              <span className="slot-title">{labels[i]}</span>
              <label className="ghost-btn small" style={{cursor:"pointer"}} data-testid={`receipt-slot-${i + 1}-gallery`}><ImagePlus size={13} /> Dari galeri<input type="file" accept="image/*" hidden onChange={e => pick(i, e)} /></label>
              <label className="ghost-btn small" style={{cursor:"pointer"}} data-testid={`receipt-slot-${i + 1}-camera`}><Camera size={13} /> Kamera<input type="file" accept="image/*" capture="environment" hidden onChange={e => pick(i, e)} /></label>
            </>}
          </div>
        );
      })}
    </div>
  );
}

export function PhotoGallery({ photos, title = "Foto lapangan", onDelete, canDelete }) {
  const [open, setOpen] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  if (!photos?.length) return <small style={{color:"#8c99a7"}}>Belum ada foto lapangan</small>;
  const close = () => { setOpen(null); setConfirming(false); };
  const doDelete = async () => { setBusy(true); try { await onDelete(open); close(); } finally { setBusy(false); } };
  return (
    <div data-testid="photo-gallery">
      <div className="photo-grid">
        {photos.map((p, i) => (
          <button type="button" key={p.id} className="photo-thumb" data-testid={`gallery-photo-${i}`} onClick={() => setOpen(p)}>
            <img src={fileUrl(p.id)} alt={p.caption || title} loading="lazy" />
            {p.lat != null && <span className="photo-geo"><MapPin size={10} /></span>}
          </button>
        ))}
      </div>
      {open && <div className="modal-backdrop" onClick={close}>
        <div className="photo-lightbox" data-testid="photo-lightbox" onClick={e => e.stopPropagation()}>
          <img src={fileUrl(open.id)} alt="" />
          <div className="photo-meta">
            <span><Clock size={12} /> {dateTime(open.taken_at)}</span>
            {open.lat != null ? <a href={`https://maps.google.com/?q=${open.lat},${open.lng}`} target="_blank" rel="noreferrer"><MapPin size={12} /> {open.lat.toFixed(5)}, {open.lng.toFixed(5)}</a> : <span><MapPin size={12} /> Tanpa GPS</span>}
            <span>oleh {open.uploaded_by} · {open.report_id || open.expense_id}</span>
            {onDelete && (!canDelete || canDelete(open)) && (confirming
              ? <span className="photo-delete-confirm" data-testid="photo-delete-confirm">Hapus foto ini? <button type="button" className="danger-btn small" disabled={busy} data-testid="photo-delete-yes" onClick={doDelete}>{busy ? "Menghapus…" : "Ya, hapus"}</button><button type="button" className="ghost-btn small" disabled={busy} data-testid="photo-delete-no" onClick={() => setConfirming(false)}>Batal</button></span>
              : <button type="button" className="icon-btn" title="Hapus foto" data-testid="photo-delete-button" onClick={() => setConfirming(true)}><Trash2 size={16} /></button>)}
            <button className="icon-btn" onClick={close} data-testid="lightbox-close"><X size={16} /></button>
          </div>
        </div>
      </div>}
    </div>
  );
}
